import {useState} from 'react';
import {generateId} from '../common/utils';
import {LocalCart, CartItemPayload, CatalogComponent} from './models';

export const useCart = () => {
    const [cart, setCart] = useState<LocalCart | undefined>(undefined);
    const [catalogItems, updateCatalogItems] = useState<Map<string, CatalogComponent[]>>(new Map());
    const [websiteId, updateWebsiteId] = useState<string | undefined>();

    const clearCart = () => {
        setCart(undefined);
    };

    const getWebsiteId = () => {
        return websiteId ?? '';
    };

    const setWebsiteId = (id: string) => updateWebsiteId(id);

    const setCatalogItems = (currCatalogItems: CatalogComponent[]) => {
        const newCatalogItemsMap = catalogItems;
        if (websiteId) {
            newCatalogItemsMap.set(websiteId, currCatalogItems);
            updateCatalogItems(newCatalogItemsMap);
        }
    };

    const getCatalogItems = () => {
        if (websiteId) {
            return catalogItems.get(websiteId) ?? [];
        } else {
            return [];
        }
    };

    const updateCart = (itemId: string, newQty: number) => {
        if (websiteId) {
            if (cart) {
                const cartItem = getCartItem(itemId);
                if (cartItem) {
                    if (newQty === 0) {
                        const cartItems = cart.cartItems;
                        cartItems.delete(itemId);
                        const updatedCart: LocalCart = {
                            ...cart,
                            ...{
                                cartItems: cartItems,
                            },
                        };
                        setCart(updatedCart);
                    } else {
                        const updatedItem: CartItemPayload = {
                            ...cartItem,
                            ...{
                                quantity: newQty,
                            },
                        };
                        const cartItems = cart.cartItems;
                        cartItems.set(itemId, updatedItem);
                        const updatedCart: LocalCart = {
                            ...cart,
                            ...{
                                cartItems: cartItems,
                            },
                        };
                        setCart(updatedCart);
                    }
                } else {
                    const websiteCatalogItems = catalogItems.get(websiteId) ?? [];
                    const cartItems = cart.cartItems;
                    const catalogItem = websiteCatalogItems.find((e) => e.id === itemId);
                    const newCartItem: CartItemPayload = {
                        id: itemId,
                        title: catalogItem?.title.text ?? '',
                        price: parseFloat(catalogItem?.price.text ?? '0'),
                        quantity: newQty,
                    };
                    cartItems.set(itemId, newCartItem);

                    const updatedCart: LocalCart = {
                        ...cart,
                        ...{
                            cartItems: cartItems,
                        },
                    };
                    setCart(updatedCart);
                }
            } else {
                const newMap = new Map<string, CartItemPayload>();
                const websiteCatalogItems = catalogItems.get(websiteId) ?? [];
                const catalogItem = websiteCatalogItems.find((e) => e.id === itemId);
                const cartItem: CartItemPayload = {
                    id: itemId,
                    title: catalogItem?.title.text ?? '',
                    price: parseFloat(catalogItem?.price.text ?? '0'),
                    quantity: newQty,
                };
                newMap.set(itemId, cartItem);
                const newCart: LocalCart = {
                    id: generateId('cart-id', 12),
                    cartItems: newMap,
                };
                setCart(newCart);
            }
        }
    };

    const getCartItem = (cartItemId: string): CartItemPayload | undefined => cart?.cartItems.get(cartItemId);

    const getCart = (): LocalCart | undefined => cart;

    const getCartAmount = (): number => {
        const cartItems = cart?.cartItems;
        if (cartItems) {
            const amount = Object.values(cartItems).reduce(
                (accumulatedSum, item: CartItemPayload) => accumulatedSum + item.price * item.quantity,
                0,
            );
            return amount;
        } else {
            return 0;
        }
    };

    return {
        cart,
        getWebsiteId,
        setWebsiteId,
        getCatalogItems,
        getCart,
        getCartAmount,
        getCartItem,
        updateCart,
        clearCart,
        setCatalogItems,
    };
};
